import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Ticket } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { homeFor } from "../components/app/Guard";
import { AuthLayout, AuthField, AuthSubmit, AuthError } from "../components/app/AuthLayout";
import { BrandLine } from "../components/Brand";

const ROLE = { coach: "Coach", player: "Player", parent: "Parent", owner: "Owner" };
// Accept the bare code or a pasted invite link (…/invite/<code> or ?token=<code>).
const cleanCode = (raw) => {
  const s = (raw || "").trim();
  const m = s.match(/[?&](?:token|code)=([^&\s]+)/) || s.match(/\/invite\/([^/?#\s]+)/);
  return (m ? m[1] : s).replace(/\s+/g, "");
};

const CodeStep = ({ code, setCode, busy, error, onSubmit }) => (
  <form onSubmit={onSubmit} className="space-y-6" data-testid="invite-code-form">
    <AuthField label="Invite code" required autoFocus data-testid="invite-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste the code from your invite" />
    <AuthError error={error} testid="invite-code-error" />
    <AuthSubmit busy={busy} busyLabel="Checking the code…" testid="invite-code-check">Continue <ArrowRight className="h-4 w-4" /></AuthSubmit>
  </form>
);

const AcceptStep = ({ info, busy, error, onSubmit, onBack }) => {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ name, password }); }} className="space-y-6" data-testid="invite-accept-form">
      <div data-testid="invite-code-team" className="rounded-2xl border border-[#39FF14]/40 bg-[#39FF14]/10 p-4">
        <p className="font-mono-gd text-[11px] uppercase tracking-[0.2em] text-[#39FF14]">You're invited</p>
        <p className="mt-1 font-display text-2xl font-black uppercase text-white">{info.team_name ?? "Your team"}</p>
        <p className="text-sm text-slate-300">{`${ROLE[info.role] ?? info.role} · ${info.email}`}</p>
      </div>
      {!info.has_account && <AuthField label="Your name" data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="First and last name" />}
      <AuthField label={info.has_account ? "Your GAMEDAY password" : "Choose a password (8+ characters)"} type="password" required minLength={info.has_account ? 1 : 8}
        autoComplete={info.has_account ? "current-password" : "new-password"} data-testid="invite-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
      {info.has_account && <p className="-mt-3 text-sm text-slate-400">You already have a GAMEDAY account with this email. Sign in to join the team.</p>}
      <AuthError error={error} testid="invite-accept-error" />
      <AuthSubmit busy={busy} busyLabel="Joining the team…" testid="invite-accept-submit">{info.has_account ? "Sign in and join" : "Create account and join"} <ArrowRight className="h-4 w-4" /></AuthSubmit>
      <button type="button" onClick={onBack} data-testid="invite-code-back" className="text-sm text-slate-400 underline hover:text-white">Use a different code</button>
    </form>
  );
};

export default function RedeemInvite() {
  const [params] = useSearchParams();
  const { login, loginWithToken } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState(params.get("code") ?? "");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const check = async (raw) => {
    const c = cleanCode(raw);
    if (!c) return;
    setBusy(true); setError("");
    try { const { data } = await api.get(`/invite/${encodeURIComponent(c)}`); setInfo({ ...data, code: c }); }
    catch (e) { setError(e?.response?.status === 404 || e?.response?.status === 400 ? "That code isn't valid or has expired. Ask your coach to send a new invite." : errMsg(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (params.get("code")) check(params.get("code")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = async ({ name, password }) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/accept-invite", { token: info.code, password, ...(name.trim() ? { name: name.trim() } : {}) });
      const user = data?.access_token ? await loginWithToken(data.access_token) : await login(info.email, password);
      nav(user ? homeFor(user) : "/login", { replace: true });
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout tagline={<>Your team<br /><span className="text-[#39FF14]">is waiting</span><br />courtside.</>}>
      <div className="space-y-6" data-testid="invite-code-page">
        <div>
          <p className="flex items-center gap-2 font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]"><Ticket className="h-4 w-4" />Coaches · Players · Parents</p>
          <h1 className="mt-3 font-display text-5xl font-black uppercase leading-none text-white">Have an invite code?</h1>
          <p className="mt-3 text-slate-400">Enter the code from the invite your coach sent through the GAMEDAY app. Your account works here and in the app.</p>
          <BrandLine className="mt-3" testid="invite-brand-line" />
        </div>
        {info ? <AcceptStep info={info} busy={busy} error={error} onSubmit={accept} onBack={() => { setInfo(null); setError(""); }} />
          : <CodeStep code={code} setCode={setCode} busy={busy} error={error} onSubmit={(e) => { e.preventDefault(); check(code); }} />}
        <p className="text-sm text-slate-400">Already on a team? <Link to="/login" data-testid="invite-login-link" className="font-semibold text-[#39FF14] hover:underline">Log in</Link></p>
      </div>
    </AuthLayout>
  );
}
